/* eslint-disable no-restricted-syntax */
import type { ColorInput, Instance as TinyColorType } from 'tinycolor2';
import tinycolor from 'tinycolor2';
import type { ClassificationMark } from 'bc-classification';
import { LevelValueEnum, MarkTypeEnum, parseClassification } from 'bc-classification';

const contrastRatio = {
  normalText: {
    AA: 4.5,
    AAA: 7,
  },
  largeText: {
    AA: 3,
    AAA: 4.5,
  },
  objects: {
    AA: 3,
  },
};

const militaryColors = {
  hostile: '#d01515',
  friendly: '#3176AF',
  neutral: '#4e8335',
  unknown: '#E4BA11',
  civilian: '#6f46a6',
} as const;

const UIColors = {
  black: '#212529',
  dark: '#17344f',
  secondary: '#5b687b',
  border: '#dde2e9',
  light: '#f8f9fa',
  white: '#FFFFFF',
} as const;

const statusColors = {
  danger: '#dc3545',
  warning: '#ffc107',
  success: '#198754',
  info: '#0f79d0',
  help: UIColors.secondary,
  disabled: tinycolor(UIColors.secondary).setAlpha(0.12).toString(),
} as const;

const focusColors = {
  focus: '#49A2DA',
  synced: '#7357ff',
} as const;

const themeColors = {
  primary: '#3170aa',
  ...UIColors,
  ...statusColors,
  ...focusColors,
  placeholder: tinycolor(UIColors.dark).setAlpha(0.35).toString(),
  overlay: tinycolor(UIColors.dark).setAlpha(0.65).toString(),
  listDefault: '#c1cbd7',
  template: '#ffd5a3',
  starterPlan: '#C4EDC4',
  locked: '#206B6F',
  lockedBackground: tinycolor('#206B6F').setAlpha(0.4).toString(),
  snapshot: '#c2e1ff',
  scrollbarThumb: 'rgba(0, 0, 0, 0.18)',
  scrollbarThumbHover: 'rgba(0, 0, 0, 0.35)',
} as const;

const classificationColors = {
  unclassified: '#007a33',
  controlled: '#502b85',
  confidential: '#0033a0',
  secret: '#c8102e',
  topSecret: '#ff8c00',
  topSecretSCI: '#fce83a',
  foreign: themeColors.secondary,
} as const;

const darkPalette = {
  purple: militaryColors.civilian,
  midnight: '#2C548F',
  blue: militaryColors.friendly,
  aqua: '#6AD2B3',
  green: militaryColors.neutral,
  yellow: militaryColors.unknown,
  orange: '#F8803C',
  red: militaryColors.hostile,
  magenta: '#EA90EA',
  brown: '#76592E',
} as const;

const lightPalette = {
  lightPurple: '#DCD0EB',
  lightMidnight: '#ADC3E5',
  lightBlue: '#C7DDEF',
  lightAqua: '#CFF2EB',
  lightGreen: '#DAEDC4',
  lightYellow: '#FAEFC1',
  lightOrange: '#FDE0D0',
  lightRed: '#FFB3B3',
  lightMagenta: '#F9DFF9',
  lightBrown: '#E7D7C1',
} as const;

const greyPalette = {
  white: themeColors.white,
  lightGrey: themeColors.light,
  grey: themeColors.border,
  midGrey: themeColors.listDefault,
  darkGrey: themeColors.secondary,
  black: themeColors.black,
} as const;

const colorPalette = { ...darkPalette, ...lightPalette, ...greyPalette } as const;

type ThemeColors = keyof typeof themeColors;
type ClassificationColors = keyof typeof classificationColors;
type ColorPalette = keyof typeof colorPalette;

const getThemeColor = (color: string): string | undefined => {
  if (color in themeColors) {
    return themeColors[color as ThemeColors];
  }
  return undefined;
};
const getClassificationColor = (color: string): string | undefined => {
  if (color in classificationColors) {
    return classificationColors[color as ClassificationColors];
  }
  return undefined;
};
const getColorPalette = (color: string): string | undefined => {
  if (color in colorPalette) {
    return colorPalette[color as ColorPalette];
  }
  return undefined;
};

function darkenBy(color: ColorInput, level = 0): TinyColorType {
  const { h, s, l, a } = tinycolor(color).toHsl();
  const darkened = l * (1 - level);
  return tinycolor({ h, s, l: darkened, a });
}

function lightenBy(color: ColorInput, level: number = 0): TinyColorType {
  const { h, s, l, a } = tinycolor(color).toHsl();
  // TODO unless I'm crazy, this rule seems to be buggy; l and level are both numbers, this should be fine
  const lightened = l + (1 - l) * level;
  return tinycolor({ h, s, l: lightened, a });
}

function mostReadable(color: ColorInput, textAlpha = 1): TinyColorType {
  return tinycolor
    .mostReadable(color, [themeColors.black, themeColors.white], {
      includeFallbackColors: false,
      level: 'AA',
      size: 'small',
    })
    .setAlpha(textAlpha);
}

function firstReadable(
  baseColor: ColorInput,
  colors: ColorInput[],
  level: number = 3
): TinyColorType | ColorInput {
  const readableColor = colors.find((color) => tinycolor.readability(baseColor, color) >= level);
  return readableColor ?? mostReadable(baseColor);
}

function contrasting(color: ColorInput, contrast = contrastRatio.normalText.AA): TinyColorType {
  const baseColor = tinycolor(String(color));
  if (baseColor.isLight()) {
    const l1 = baseColor.getLuminance();
    const l2 = Math.max(0, (l1 + 0.05) / contrast - 0.05);
    while (baseColor.darken().getLuminance() > l2);
    return baseColor;
  }
  const l2 = baseColor.getLuminance();
  const l1 = Math.min(1, contrast * (l2 + 0.05) - 0.05);
  while (baseColor.lighten().getLuminance() < l1);
  return baseColor;
}

function readableTextColor(
  color: ColorInput,
  bg: ColorInput = '#fff',
  contrast = contrastRatio.normalText.AA
): TinyColorType {
  let textColor = tinycolor(String(color));
  const bgColor = tinycolor(String(bg));
  const { h, s, l } = textColor.toHsl();

  let increase = 0.01;
  let lums = l;

  if (bgColor.isLight()) {
    increase *= -1;
  }

  while (tinycolor.readability(textColor, bg) < contrast && lums >= 0 && lums <= 1) {
    lums += increase;
    textColor = tinycolor({ h, s, l: lums });
  }

  return textColor;
}

/** Contrast ratio below which a background is considered "essentially white". */
const NEAR_WHITE_THRESHOLD = 1.1;

/**
 * Returns a readable link color for the given background.
 * On white/near-white backgrounds, adjusts the link color for contrast.
 * On colored backgrounds, falls back to the most-readable text color
 * so links don't clash with the background hue.
 */
function readableLinkColor(
  backgroundColor: ColorInput,
  linkColor: ColorInput = '#0d6efd'
): TinyColorType {
  const isNearWhite = tinycolor.readability(backgroundColor, '#fff') < NEAR_WHITE_THRESHOLD;
  return isNearWhite
    ? readableTextColor(linkColor, backgroundColor)
    : mostReadable(backgroundColor);
}

function haloColor(color: ColorInput, halo: ColorInput): string {
  return (tinycolor.readability(color, halo) >= 1.85 ? halo : contrasting(halo)).toString();
}

function setAlpha(color: ColorInput, alpha: number): string {
  return tinycolor(color).setAlpha(alpha).toString();
}

function colorContrast(color: ColorInput, bgColor: ColorInput = '#fff'): number {
  return tinycolor.readability(color, bgColor);
}

function mark2Color(mark: ClassificationMark): string | undefined {
  if (mark.type === MarkTypeEnum.FOREIGN) {
    return classificationColors.foreign;
  }
  if (mark.cui) {
    return classificationColors.controlled;
  }
  switch (mark.classificationLevel) {
    case LevelValueEnum.UNCLASSIFIED:
      return classificationColors.unclassified;
    case LevelValueEnum.CONFIDENTIAL:
    case LevelValueEnum.RESTRICTED:
      return classificationColors.confidential;
    case LevelValueEnum.SECRET:
      return classificationColors.secret;
    case LevelValueEnum.TOP_SECRET:
      if (mark.sci?.length) {
        return classificationColors.topSecretSCI;
      }
      return classificationColors.topSecret;
    default:
      break;
  }
  return undefined;
}

function classification2Color(classification?: string | null): string | undefined {
  if (!classification || classification === undefined) return undefined;

  const mark = parseClassification(classification);
  return mark2Color(mark);
}

export type { ThemeColors, ClassificationColors, ColorPalette };
export {
  contrasting,
  contrastRatio,
  darkenBy,
  lightenBy,
  mostReadable,
  mark2Color,
  classification2Color,
  classificationColors,
  themeColors,
  firstReadable,
  haloColor,
  setAlpha,
  colorContrast,
  readableLinkColor,
  readableTextColor,
  darkPalette,
  lightPalette,
  greyPalette,
  colorPalette,
  militaryColors,
  UIColors,
  focusColors,
  statusColors,
  getThemeColor,
  getClassificationColor,
  getColorPalette,
};
